import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import * as stateActions from '../../actions/stateActions';
import * as sessionStates from '../../sessionStates';
import { styled } from '@mui/material/styles';
import { withMediaSipContext } from '../../contexts/MediaSipContext';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

const PaperStyled = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(2)
}));

const IconGreen = styled(IconButton)({
	color: 'green'
});

const IconRed = styled(IconButton)({
	color: 'red'
});

const Sessions = (props) =>
{
	const { sipCaller } = props;

	const dispatch = useDispatch();
	const sessions = useSelector(state => state.sessions);
	const currentSession = useSelector(state => state.userStatus.currentSession);

	const setCurrentSession = (callId) => {
		dispatch(stateActions.setCurrentSession({ currentSession: callId }));
	};

	return (
		<PaperStyled>
			{ Object.keys(sessions).length > 0 ?
				<List>
					{ Object.keys(sessions).map((callId) =>
					{
						const session = sessions[callId];
						const sipSession = session.sipSession;

						// Safely access remote identity with JsSIP compatibility
						let displayName, sipUri;

						try {
							// JsSIP uses remote_identity instead of remoteIdentity
							const remoteIdentity = sipSession.remoteIdentity || sipSession.remote_identity;

							if (remoteIdentity) {
								// Handle displayName from various possible structures
								displayName =
									remoteIdentity.display_name || // JsSIP format
									remoteIdentity.displayName ||  // SIP.js format
									(remoteIdentity.uri && remoteIdentity.uri.user) || // SIP.js format
									(remoteIdentity.uri && remoteIdentity.uri.toString()) || // Fallback
									'Unknown Caller'; // Last resort

								// Handle SIP URI from various possible structures
								sipUri = remoteIdentity.uri ?
									(typeof remoteIdentity.uri.toString === 'function' ?
										remoteIdentity.uri.toString() :
										remoteIdentity.uri) :
									'Unknown URI';
							} else {
								// If we can't find any remote identity, use callId as fallback
								displayName = `Call ${callId.substring(0, 8)}...`;
								sipUri = `Session ID: ${callId}`;
							}
						} catch (error) {
							console.error('Error accessing session remote identity:', error);
							displayName = `Call ${callId.substring(0, 8)}...`;
							sipUri = `Session ID: ${callId}`;
						}

						const answer =
							session.direction === sessionStates.INCOMING &&
							(session.sessionState === sessionStates.NEW ||
							 session.sessionState === sessionStates.ESTABLISHING);

						return (
							<ListItem
								key={callId}
								button
								selected={callId === currentSession}
								onClick={() => setCurrentSession(callId)}
								disabled={session.sessionState === sessionStates.TERMINATED}
							>
								<ListItemText
									primary={displayName}
									secondary={sipUri}
								/>
								{answer ? (
									<IconGreen
										aria-label='Answer'
										size='large'
										onClick={() => sipCaller.accept(sipSession)}
									>
										<CallIcon />
									</IconGreen>
								) : (
									<IconRed
										aria-label='Hangup'
										size='large'
										onClick={() => sipCaller.terminate(sipSession)}
									>
										<CallEndIcon />
									</IconRed>
								)}
							</ListItem>
						);
					})}
				</List>
				:
				<Typography
					color='inherit'
					noWrap
				>
					No sessions
				</Typography>
			}
		</PaperStyled>
	);
};

Sessions.propTypes =
{
	sipCaller : PropTypes.object.isRequired
};

export default withMediaSipContext(Sessions);
